// Scan-to-CAD service client — the frontend side of the autonomous scan→STEP
// feature (mesh in → parametric STEP out). Mirrors the two-hop TK pattern
// (browser → edge fn → microservice): the browser never calls the recon
// microservice directly, it invokes the `scan-to-cad` edge function, which
// proxies to `recon-microservice` (X-TK-Auth) and tracks a job.
//
// The job exposes REAL progress: the recon pipeline has six discrete stages
// (Stage 0-5), so unlike the fal image-to-CAD path (which fakes a timer), the
// bar here reflects the actual stage the reconstruction is in.
//
// The edge function is not deployed yet (it needs the recon Fly app). Until it
// is, a DEV-only simulator (import.meta.env.DEV) drives the full flow so the UX
// is demoable in preview; production ships flag-gated OFF (`scan-to-cad`).

import { supabase } from '@/lib/supabase';

import {
  SCAN_STAGE_KEYS,
  type ScanStageKey,
} from '@/components/scanToCad/scanStages';
import type { ScanOverlay } from '@/components/scanToCad/overlayTypes';

export type { ScanOverlay } from '@/components/scanToCad/overlayTypes';

export type ScanJobStatus = 'queued' | 'processing' | 'success' | 'failure';

export type ScanUnits = 'mm' | 'cm' | 'm' | 'in';

export interface ScanJobProgress {
  status: ScanJobStatus;
  /** 0–1 overall progress across the pipeline — REAL, from the backend. */
  progress: number;
  /** Current pipeline stage key, or null before it starts. */
  stage: ScanStageKey | null;
  message?: string;
}

export interface ScanJobResult extends ScanJobProgress {
  /** Reconstructed STEP file, base64 (the download). */
  stepB64?: string;
  /** Signed URL to a renderable STL preview (e.g. a committed harness fixture). */
  stlUrl?: string;
  /** Renderable STL preview of the reconstruction, base64 (live-job inline path,
   *  same pattern as stepB64). Prefer stlUrl when both are present. */
  previewStlB64?: string;
  /** Segmentation + fit + deviation overlay for the Live Recon Canvas (P0). */
  overlay?: ScanOverlay;
  /** Machine-readable A–F reconstruction grade. */
  grade?: string;
  /** Deviation of the reconstruction vs the input scan, mm (95th pct). */
  deviationMm?: number;
  /** Behavior stamp (§9.4.5): which roster + thresholds produced this result. */
  behavior?: ScanBehavior;
  error?: string;
}

/** Provenance of a reconstruction — the roster version + numeric thresholds that
 *  produced it (mirrors the recon RosterConfig; carried through for auditability). */
export interface ScanBehavior {
  rosterVersion?: string;
  thresholds?: Record<string, number>;
}

export interface CreateScanJobInput {
  /** meshRef in the private `meshes` bucket (from useScanUpload). */
  meshRef: string;
  /** Declared units of the scan — the recon pipeline REJECTS unit-less input. */
  units: ScanUnits;
}

const USE_SIMULATOR = import.meta.env.DEV;

export async function createScanJob(
  input: CreateScanJobInput,
): Promise<{ jobId: string }> {
  if (USE_SIMULATOR) return simCreate(input);
  const { data, error } = await supabase.functions.invoke('scan-to-cad', {
    body: { op: 'create', ...input },
  });
  if (error) throw new Error(error.message);
  return data as { jobId: string };
}

export async function getScanJob(jobId: string): Promise<ScanJobResult> {
  if (USE_SIMULATOR) return simStatus(jobId);
  const { data, error } = await supabase.functions.invoke('scan-to-cad', {
    body: { op: 'status', jobId },
  });
  if (error) throw new Error(error.message);
  return data as ScanJobResult;
}

// ---------------------------------------------------------------------------
// Editing session (workbench P2) — open a live session from a finished job,
// then override a region's fitted type and re-render. Two-hop as always:
// browser → scan-to-cad edge fn → recon /session* endpoints.
// ---------------------------------------------------------------------------

export interface ScanSession {
  sessionId: string;
  overlay: ScanOverlay;
  /** The per-region types the toolbar may force (plane/cylinder/…/torus/freeform). */
  overrideTypes: string[];
  /** Preview STL (base64) of the current edited shape, so the solid re-syncs. */
  previewStlB64?: string;
  /** Behavior stamp (§9.4.5): the session fits on the V2 roster. */
  behavior?: ScanBehavior;
}

/** A session edit's result: the new overlay + a re-synced preview solid. */
export interface ScanEditResult {
  overlay: ScanOverlay;
  previewStlB64?: string;
}

export async function openScanSession(jobId: string): Promise<ScanSession> {
  if (USE_SIMULATOR) return simOpenSession(jobId);
  const { data, error } = await supabase.functions.invoke('scan-to-cad', {
    body: { op: 'open-session', jobId },
  });
  if (error) throw new Error(error.message);
  const d = data as {
    sessionId: string;
    overlay: ScanOverlay;
    overrideTypes?: string[];
    previewStlB64?: string;
    behavior?: ScanBehavior;
  };
  return {
    sessionId: d.sessionId,
    overlay: d.overlay,
    overrideTypes: d.overrideTypes ?? [],
    previewStlB64: d.previewStlB64,
    behavior: d.behavior,
  };
}

export async function refitRegion(
  sessionId: string,
  regionId: number,
  forceType: string,
): Promise<ScanEditResult> {
  if (USE_SIMULATOR) return simRefitRegion(sessionId, regionId, forceType);
  const { data, error } = await supabase.functions.invoke('scan-to-cad', {
    body: { op: 'refit-region', sessionId, regionId, forceType },
  });
  if (error) throw new Error(error.message);
  return data as ScanEditResult;
}

/** Merge two or more regions into one, refitting the union as a single primitive.
 *  The one op that changes the session's (otherwise frozen) region membership. */
export async function mergeRegions(
  sessionId: string,
  regionIds: number[],
): Promise<ScanEditResult> {
  if (USE_SIMULATOR) return simMergeRegions(sessionId, regionIds);
  const { data, error } = await supabase.functions.invoke('scan-to-cad', {
    body: { op: 'merge-regions', sessionId, regionIds },
  });
  if (error) throw new Error(error.message);
  return data as ScanEditResult;
}

/** Split one region into two (the inverse of merge) — for UNDER-segmentation,
 *  where the auto-segmenter lumped two physical faces into one region. Clusters
 *  the region's points by normal direction and re-fits each half. */
export async function splitRegion(
  sessionId: string,
  regionId: number,
): Promise<ScanEditResult> {
  if (USE_SIMULATOR) return simSplitRegion(sessionId, regionId);
  const { data, error } = await supabase.functions.invoke('scan-to-cad', {
    body: { op: 'split-region', sessionId, regionId },
  });
  if (error) throw new Error(error.message);
  return data as ScanEditResult;
}

/** Strictness slider bounds (the χ²ν acceptance gate) — mirrors the recon
 *  session's _STRICTNESS_MIN/_MAX; 4.0 is the autonomous default. */
export const STRICTNESS = { min: 2, max: 8, default: 4 } as const;

/** Global re-fit at a chosen strictness (χ²ν acceptance gate). Lower = stricter
 *  (cleaner primitives → more freeform); higher = looser (more primitives). This
 *  RESETS per-region overrides — it re-runs the automatic fit at this strictness. */
export async function setStrictness(
  sessionId: string,
  consistencyMax: number,
): Promise<ScanEditResult> {
  if (USE_SIMULATOR) return simSetStrictness(sessionId);
  const { data, error } = await supabase.functions.invoke('scan-to-cad', {
    body: { op: 'set-strictness', sessionId, consistencyMax },
  });
  if (error) throw new Error(error.message);
  return data as ScanEditResult;
}

/** A discovered geometric relation between fits (the accept/reject chip). */
export interface ScanConstraint {
  id: number;
  kind: string; // 'coaxial' | 'parallel' | 'equal_radius'
  members: number[];
  value: number | null;
}

/** A derived datum feature (reference geometry) — plane / axis / point. */
export interface ScanDatum {
  id: number;
  label: string; // 'A' | 'B' | 'C' …
  kind: 'plane' | 'axis' | 'point';
  members: number[]; // supporting region ids
  support: number; // total supporting points
  point: number[]; // a point on the datum (all kinds)
  normal?: number[]; // plane datums
  axis?: number[]; // axis datums
}

export async function listDatums(
  sessionId: string,
): Promise<{ datums: ScanDatum[] }> {
  if (USE_SIMULATOR) return simListDatums(sessionId);
  const { data, error } = await supabase.functions.invoke('scan-to-cad', {
    body: { op: 'list-datums', sessionId },
  });
  if (error) throw new Error(error.message);
  return data as { datums: ScanDatum[] };
}

export async function listConstraints(
  sessionId: string,
): Promise<{ constraints: ScanConstraint[] }> {
  if (USE_SIMULATOR) return simListConstraints(sessionId);
  const { data, error } = await supabase.functions.invoke('scan-to-cad', {
    body: { op: 'list-constraints', sessionId },
  });
  if (error) throw new Error(error.message);
  return data as { constraints: ScanConstraint[] };
}

export async function applyConstraints(
  sessionId: string,
  accepted: number[],
): Promise<ScanEditResult> {
  if (USE_SIMULATOR) return simApplyConstraints(sessionId);
  const { data, error } = await supabase.functions.invoke('scan-to-cad', {
    body: { op: 'apply-constraints', sessionId, accepted },
  });
  if (error) throw new Error(error.message);
  return data as ScanEditResult;
}

export async function exportScanSession(
  sessionId: string,
): Promise<{ stepB64: string }> {
  if (USE_SIMULATOR) return { stepB64: PLACEHOLDER_STEP_B64 };
  const { data, error } = await supabase.functions.invoke('scan-to-cad', {
    body: { op: 'session-export', sessionId },
  });
  if (error) throw new Error(error.message);
  return data as { stepB64: string };
}

// ---------------------------------------------------------------------------
// DEV simulator (import.meta.env.DEV only) — walks a job through the six real
// pipeline stages on a wall clock so the UI can be exercised end-to-end in
// preview before the edge function + recon Fly app exist. Never runs in prod.
// ---------------------------------------------------------------------------

const SIM_TOTAL_MS = 12000;
const simJobs = new Map<string, number>(); // jobId -> start time (ms)

function simCreate(_input: CreateScanJobInput): { jobId: string } {
  const jobId = `sim-${Math.random().toString(36).slice(2)}`;
  simJobs.set(jobId, performance.now());
  return { jobId };
}

// The DEV success payload is seeded from a committed P0 harness fixture (a real
// reconstruction's overlay + preview STL) so the Live Recon Canvas is fully
// demoable in preview without the edge fn / recon Fly app.
const SIM_FIXTURE = 'boss_on_plate';
let simOverlayCache: ScanOverlay | null | undefined; // undefined = not yet fetched

async function loadSimOverlay(): Promise<ScanOverlay | undefined> {
  if (simOverlayCache !== undefined) return simOverlayCache ?? undefined;
  try {
    const r = await fetch(
      `${import.meta.env.BASE_URL}scan-harness/${SIM_FIXTURE}.json`,
    );
    simOverlayCache = r.ok ? ((await r.json()) as ScanOverlay) : null;
  } catch {
    simOverlayCache = null;
  }
  return simOverlayCache ?? undefined;
}

// DEV session simulator — a client-side per-region override on a cloned fixture
// overlay: it recolors + relabels the picked region so the toolbar interaction is
// fully demoable in preview. (The real per-region GEOMETRY re-fit happens
// server-side in prod via recon /session/{id}/refit-region.)
const SIM_REGION_HEX: Record<string, string> = {
  plane: '#4C8DF5',
  cylinder: '#3FBF6F',
  sphere: '#F5B24C',
  cone: '#A46CF5',
  torus: '#F56C9C',
  freeform: '#9AA0A6',
};
const SIM_OVERRIDE_TYPES = [
  'plane',
  'cylinder',
  'sphere',
  'cone',
  'torus',
  'freeform',
];
const simSessions = new Map<string, ScanOverlay>();

async function simOpenSession(_jobId: string): Promise<ScanSession> {
  const base = await loadSimOverlay();
  const overlay = JSON.parse(
    JSON.stringify(
      base ?? {
        grade: null,
        deviationMm: null,
        rung: 0,
        regions: [],
        cloud: { count: 0, positions: [], regionIds: [], deviation: [] },
      },
    ),
  ) as ScanOverlay;
  const sessionId = `sim-sess-${Math.random().toString(36).slice(2)}`;
  simSessions.set(sessionId, overlay);
  return { sessionId, overlay, overrideTypes: SIM_OVERRIDE_TYPES };
}

async function simRefitRegion(
  sessionId: string,
  regionId: number,
  forceType: string,
): Promise<{ overlay: ScanOverlay }> {
  const overlay = simSessions.get(sessionId);
  if (!overlay) throw new Error('unknown session');
  const region = overlay.regions.find((r) => r.id === regionId);
  if (region) {
    region.kind = forceType;
    region.color = SIM_REGION_HEX[forceType] ?? region.color;
  }
  return { overlay: JSON.parse(JSON.stringify(overlay)) as ScanOverlay };
}

// DEV merge simulator — collapse the picked regions into one (largest member
// sets the kind/color), renumber ids to list positions, remap the cloud's
// per-point region ids. Mirrors the server's frozen-membership merge.
async function simMergeRegions(
  sessionId: string,
  regionIds: number[],
): Promise<ScanEditResult> {
  const ov = simSessions.get(sessionId);
  if (!ov) throw new Error('unknown session');
  const idset = new Set(regionIds);
  const merged = ov.regions.filter((r) => idset.has(r.id));
  if (merged.length < 2)
    return { overlay: JSON.parse(JSON.stringify(ov)) as ScanOverlay };
  const keep = ov.regions.filter((r) => !idset.has(r.id));
  const primary = merged.reduce((a, b) =>
    b.pointCount > a.pointCount ? b : a,
  );
  const newRegion = {
    ...primary,
    pointCount: merged.reduce((s, r) => s + r.pointCount, 0),
  };
  const newRegions = [...keep, newRegion];
  const newIndex = newRegions.length - 1;
  const oldToNew = new Map<number, number>();
  newRegions.forEach((r, k) => oldToNew.set(r.id, k));
  merged.forEach((r) => oldToNew.set(r.id, newIndex));
  newRegions.forEach((r, k) => {
    r.id = k;
  });
  ov.regions = newRegions;
  ov.cloud.regionIds = ov.cloud.regionIds.map((rid) =>
    rid < 0 ? rid : (oldToNew.get(rid) ?? rid),
  );
  return { overlay: JSON.parse(JSON.stringify(ov)) as ScanOverlay };
}

// DEV split sim — the real op clusters the region's points by normal server-side;
// the sim approximates by splitting the region's cloud points at their median X
// into two regions, then renumbers ids to list positions (like the backend).
async function simSplitRegion(
  sessionId: string,
  regionId: number,
): Promise<ScanEditResult> {
  const ov = simSessions.get(sessionId);
  if (!ov) throw new Error('unknown session');
  const region = ov.regions.find((r) => r.id === regionId);
  const ids = ov.cloud.regionIds;
  const pos = ov.cloud.positions;
  if (!region)
    return { overlay: JSON.parse(JSON.stringify(ov)) as ScanOverlay };
  const members: number[] = [];
  for (let i = 0; i < ids.length; i++) if (ids[i] === regionId) members.push(i);
  if (members.length < 4)
    return { overlay: JSON.parse(JSON.stringify(ov)) as ScanOverlay };
  const xs = members.map((i) => pos[i * 3]).sort((a, b) => a - b);
  const medX = xs[Math.floor(xs.length / 2)];
  const newId = Math.max(...ov.regions.map((r) => r.id)) + 1;
  let aCount = 0;
  let bCount = 0;
  for (const i of members) {
    if (pos[i * 3] > medX) {
      ids[i] = newId;
      bCount += 1;
    } else aCount += 1;
  }
  region.pointCount = aCount;
  ov.regions.push({ ...region, id: newId, pointCount: bCount });
  const oldToNew = new Map<number, number>();
  ov.regions.forEach((r, k) => oldToNew.set(r.id, k));
  ov.cloud.regionIds = ids.map((rid) =>
    rid < 0 ? rid : (oldToNew.get(rid) ?? rid),
  );
  ov.regions.forEach((r, k) => {
    r.id = k;
  });
  return { overlay: JSON.parse(JSON.stringify(ov)) as ScanOverlay };
}

/** DEV/harness only: open a sim session from an already-loaded overlay, so the
 *  workbench harness can drive the INTERACTIVE canvas over its selected fixture
 *  (aligning the session with the shown part, and registering it for refit). */
export function devSessionFromOverlay(overlay: ScanOverlay): ScanSession {
  const cloned = JSON.parse(JSON.stringify(overlay)) as ScanOverlay;
  const sessionId = `sim-sess-${Math.random().toString(36).slice(2)}`;
  simSessions.set(sessionId, cloned);
  return { sessionId, overlay: cloned, overrideTypes: SIM_OVERRIDE_TYPES };
}

function simListDatums(sessionId: string): { datums: ScanDatum[] } {
  const ov = simSessions.get(sessionId);
  const regions = ov?.regions ?? [];
  const datums: ScanDatum[] = [];
  let id = 0;
  const push = (
    kind: ScanDatum['kind'],
    members: number[],
    extra: Partial<ScanDatum>,
  ) => {
    datums.push({
      id,
      label: String.fromCharCode(65 + id),
      kind,
      members,
      support: members.length,
      point: [0, 0, 0],
      ...extra,
    });
    id += 1;
  };
  const axisRegions = regions
    .filter((r) => r.kind === 'cylinder' || r.kind === 'cone')
    .map((r) => r.id);
  if (axisRegions.length)
    push('axis', axisRegions, { axis: [0, 0, 1], point: [0, 0, 0] });
  const planeRegions = regions
    .filter((r) => r.kind === 'plane')
    .map((r) => r.id);
  if (planeRegions.length)
    push('plane', planeRegions, { normal: [0, 0, 1], point: [0, 0, 0] });
  return { datums };
}

function simListConstraints(sessionId: string): {
  constraints: ScanConstraint[];
} {
  const ov = simSessions.get(sessionId);
  const planes = (ov?.regions ?? [])
    .filter((r) => r.kind === 'plane')
    .map((r) => r.id);
  const constraints: ScanConstraint[] = [];
  if (planes.length >= 2)
    constraints.push({
      id: 0,
      kind: 'parallel',
      members: planes.slice(0, 3),
      value: null,
    });
  return { constraints };
}

async function simApplyConstraints(sessionId: string): Promise<ScanEditResult> {
  const ov = simSessions.get(sessionId);
  return { overlay: JSON.parse(JSON.stringify(ov)) as ScanOverlay };
}

// DEV strictness sim — the real op re-fits every region at the chosen χ²ν gate
// server-side; the sim just echoes the current overlay (no client-side re-fit).
async function simSetStrictness(sessionId: string): Promise<ScanEditResult> {
  const ov = simSessions.get(sessionId);
  return { overlay: JSON.parse(JSON.stringify(ov)) as ScanOverlay };
}

async function simStatus(jobId: string): Promise<ScanJobResult> {
  const start = simJobs.get(jobId);
  if (start === undefined)
    return {
      status: 'failure',
      progress: 0,
      stage: null,
      error: 'unknown job',
    };
  const frac = Math.min(1, (performance.now() - start) / SIM_TOTAL_MS);
  const stageIdx = Math.min(
    SCAN_STAGE_KEYS.length - 1,
    Math.floor(frac * SCAN_STAGE_KEYS.length),
  );
  if (frac >= 1) {
    simJobs.delete(jobId);
    const overlay = await loadSimOverlay();
    return {
      status: 'success',
      progress: 1,
      stage: SCAN_STAGE_KEYS[SCAN_STAGE_KEYS.length - 1],
      stepB64: PLACEHOLDER_STEP_B64,
      grade: overlay?.grade ?? 'B',
      deviationMm: overlay?.deviationMm ?? 0.14,
      message: 'Reconstruction complete',
      overlay,
      stlUrl: `${import.meta.env.BASE_URL}scan-harness/${SIM_FIXTURE}.stl`,
    };
  }
  return {
    status: 'processing',
    progress: frac,
    stage: SCAN_STAGE_KEYS[stageIdx],
    message: 'Reconstructing…',
  };
}

// A minimal but valid ISO-10303-21 (STEP AP242) header-only file, base64, so the
// DEV download produces a real .step file. Replaced by the real reconstruction
// in production.
const PLACEHOLDER_STEP =
  `ISO-10303-21;\nHEADER;\nFILE_DESCRIPTION(('Scan-to-CAD DEV placeholder'),'2;1');\n` +
  `FILE_NAME('scan.step','',(''),(''),'recon','scan-to-cad','');\n` +
  `FILE_SCHEMA(('AUTOMOTIVE_DESIGN'));\nENDSEC;\nDATA;\nENDSEC;\nEND-ISO-10303-21;\n`;
const PLACEHOLDER_STEP_B64 =
  typeof btoa !== 'undefined' ? btoa(PLACEHOLDER_STEP) : '';
